import { useState } from "react";

/**
 * A hook for handling sorting functionality
 * @param {Object} options - Options for the hook
 * @param {number|null} options.initialSortBy - Initial column index to sort by
 * @param {string|null} options.initialSortDirection - Initial sort direction ('asc' or 'desc')
 * @param {Function} options.getSortValue - Function to get the value to sort by from an item and column index
 * @param {Function} options.getDefaultSortValue - Function to get the default sort value from an item
 * @returns {Object} - Sorting state and functions
 */
export default function useSorting({
  initialSortBy = null,
  initialSortDirection = null,
  getSortValue = (item, columnIndex) => item[columnIndex],
  getDefaultSortValue = item => item.name
} = {}) {
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);

  /**
   * Handle sorting when a column header is clicked
   * @param {number} columnIndex - Index of the column to sort by
   */
  const handleSort = columnIndex => {
    if (sortBy === columnIndex) {
      // If already sorting by this column, cycle through sort directions
      if (sortDirection === "desc") {
        // Switch to ascending
        setSortDirection("asc");
      } else if (sortDirection === "asc") {
        // Reset to default sorting (by name)
        setSortBy(null);
        setSortDirection(null);
      }
    } else {
      // Start with descending sort for a new column
      setSortBy(columnIndex);
      setSortDirection("desc");
    }
  };

  /**
   * Sort an array of items based on the current sort settings
   * @param {Array} items - Array of items to sort
   * @returns {Array} - Sorted array
   */
  const sortItems = items => {
    if (!items || !items.length) return items;

    const sortedItems = [...items];

    if (sortBy !== null && sortDirection !== null) {
      sortedItems.sort((a, b) => {
        const aValue = getSortValue(a, sortBy);
        const bValue = getSortValue(b, sortBy);

        if (sortDirection === "desc") {
          return bValue - aValue; // Descending order
        } else {
          return aValue - bValue; // Ascending order
        }
      });
    } else {
      // Default sort
      sortedItems.sort((a, b) => {
        const aValue = getDefaultSortValue(a);
        const bValue = getDefaultSortValue(b);

        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue);
        } else {
          return aValue - bValue;
        }
      });
    }

    return sortedItems;
  };

  return {
    sortBy,
    sortDirection,
    handleSort,
    sortItems
  };
}
